import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface AcceptDeclineSectionProps {
  hasViewedLOA: boolean;
  onAccept: () => void;
  onDecline: (reason: string) => void;
  sectionStyle: any;
  disabled?: boolean;
}

const AcceptDeclineSection: React.FC<AcceptDeclineSectionProps> = ({
  hasViewedLOA,
  onAccept,
  onDecline,
  sectionStyle,
  disabled = false
}) => {
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const handleDeclineClick = () => {
    setShowDeclineDialog(true);
  };

  const handleDeclineConfirm = () => {
    if (declineReason.trim()) {
      onDecline(declineReason);
      setShowDeclineDialog(false);
    }
  };

  const handleDeclineCancel = () => {
    setShowDeclineDialog(false);
    setDeclineReason('');
  };

  return (
    <>
      <Box sx={sectionStyle}>
        <Typography variant="h6" gutterBottom color="primary">
          Accept or Decline
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={3}>
          {hasViewedLOA 
            ? 'Please review the document and choose to accept or decline the Letter of Authority.'
            : 'Please view the LOA document above before accepting or declining.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={onAccept}
            disabled={!hasViewedLOA || disabled}
            size="large"
            sx={{ minWidth: 150 }}
          >
            Accept LOA
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleDeclineClick}
            disabled={!hasViewedLOA || disabled}
            size="large"
            sx={{ minWidth: 150 }}
          >
            Decline LOA
          </Button>
        </Box>
      </Box>

      {/* Decline Reason Dialog */}
      <Dialog open={showDeclineDialog} onClose={handleDeclineCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Decline Letter of Authority</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Please provide a reason for declining this Letter of Authority:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Enter your reason here..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeclineCancel}>Cancel</Button>
          <Button 
            onClick={handleDeclineConfirm} 
            variant="contained" 
            color="error"
            disabled={!declineReason.trim()}
          >
            Confirm Decline
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AcceptDeclineSection;
